import * as _ from 'lodash';
import ErrorDisplay from '../../../components/ErrorDisplay';
import Loading from '../../../components/Loading';
import SlotsLayout from './SlotsLayout';
import { addRangeToPeriod, addRangeToPeriods } from '@opanuj/shared/utils';
import { AppSettings, LessonExpanded, SlotExpanded } from '@opanuj/shared/types';
import { findRoomAvailabilityConflict, findSlotAvailabilityIssue, isSlotBlocked } from '@opanuj/shared/slots';
import { ref } from 'firebase/database';
import { setDocumentTitle } from '../../../utils/documentTitle';
import { useClientsList } from '../../../hooks/useClients';
import { useDatabase } from '../../../hooks/useDatabase';
import { useEffect } from 'react';
import { useObject } from 'react-firebase-hooks/database';
import { useOffer } from '../../../hooks/useOffer';
import { useSettings } from '../../../hooks/useSettings';
import { useSlots } from '../../../hooks/useSlots';
import { useTeachers } from '../../../hooks/useTeachers';

type SlotAvailabilityStatus = boolean | {
	reason: string;
	data: { slotData: SlotExpanded };
};

type SlotViewModel = {
	lessons?: any[];
	isRoomAvailable: SlotAvailabilityStatus;
	isTeacherAvailable: SlotAvailabilityStatus;
	[key: string]: any;
};

const buildSlotData = (slots: any, teachers: any, offer: any): SlotExpanded[] =>
	addRangeToPeriods(
		_.map(slots, (slot) => ({
			defaultTeacherData: teachers[slot.defaultTeacher],
			offerItemData: offer[slot.code],
			...slot,
		}))
	) as SlotExpanded[];

const buildCurrentLessons = (registrations: any, slots: any, teachers: any, offer: any): LessonExpanded[] =>
	_.reduce(
		_.values(registrations),
		(acc, registration) => {
			for (const registrationLesson of registration.lessons) {
				const lessonMatch = _.find(acc, {
					slot: registrationLesson.slot,
					teacher: registrationLesson.teacher,
				});

				if (lessonMatch) {
					lessonMatch.participants.push(registration.client);
					continue;
				}

				if (!slots[registrationLesson.slot]) {
					console.error('NO SLOT FOR:', registrationLesson);
				}

				acc.push({
					slot: registrationLesson.slot,
					teacher: registrationLesson.teacher,
					participants: [registration.client],
					teacherData: teachers[registrationLesson.teacher],
					slotData: addRangeToPeriod(slots[registrationLesson.slot]),
					offerItemData: offer[slots[registrationLesson.slot].code],
				});
			}

			return acc;
		},
		[] as LessonExpanded[]
	);

const buildExtendedSlots = (slots = {}, registrations = {}, clients: any[] = []) =>
	Object.values(slots).map((slot: any) => {
		const nextSlot = {
			...slot,
			...addRangeToPeriod(slot),
			reservations: slot.reservations?.map((reservation: any) => ({
				...reservation,
				client: clients.find((candidate) => candidate.uid === reservation.uid || candidate.id === reservation.clientId),
			})),
		};

		// since above is only linking lessons with reservations for some reason...
		for (const lesson of nextSlot.lessons ?? []) {
			for (const student of lesson.students ?? []) {
				student.client = clients.find((candidate) => candidate.uid === student.uid || candidate.id === student.clientId) ?? student.client;
			}
		}

		return nextSlot as SlotViewModel;
	});

const getRoomAvailabilityStatus = (
	slotData: SlotExpanded | undefined,
	lessons: LessonExpanded[]
): SlotAvailabilityStatus => {
	if (!slotData) return true;

	const conflict = findRoomAvailabilityConflict(slotData, lessons);
	if (!conflict) return true;

	return {
		reason: 'pokój zajęty',
		data: { slotData: conflict.slotData },
	};
};

const getTeacherAvailabilityStatus = (
	teacherName: string,
	slotData: SlotExpanded | undefined,
	lessons: LessonExpanded[]
): SlotAvailabilityStatus => {
	if (!slotData) return false;

	const issue = findSlotAvailabilityIssue(slotData, lessons.filter((lesson) => lesson.teacher === teacherName));
	if (!issue) return false;

	return {
		reason: issue.reason,
		data: { slotData: issue.lesson.slotData },
	};
};

function SlotsPage() {
	useEffect(setDocumentTitle('Terminy')); // eslint-disable-line react-hooks/exhaustive-deps

	const database = useDatabase();
	const [clients, clientsLoading, clientsError] = useClientsList();
	const [reservationsObj, reservationsLoading, reservationsError] = useObject(ref(database, 'bookings'));
	const [slots, slotsLoading, slotsError] = useSlots();
	const [teachers, teachersLoading, teachersError] = useTeachers();
	const [offer, offerLoading, offerError] = useOffer();
	const [settings, settingsLoading, settingsError] = useSettings();

	if (clientsLoading || reservationsLoading || offerLoading || slotsLoading || teachersLoading || settingsLoading) {
		return <Loading/>;
	}

	const error = clientsError || reservationsError || offerError || slotsError || teachersError || settingsError;
	if (error) return <ErrorDisplay error={error}/>;

	const data = {
		slots: slots!,
		teachers: teachers!,
		offer: offer!,
		registrations: reservationsObj?.val() ?? [],
		clients: clients!,
	};

	const slotsData = buildSlotData(data.slots, data.teachers, data.offer);
	const currentLessons = buildCurrentLessons(data.registrations, data.slots, data.teachers, data.offer);
	const extendedSlots = buildExtendedSlots(data.slots, data.registrations, data.clients);

	extendedSlots.forEach((slot: any) => {
		const slotData = slotsData.find((candidate) => candidate.id === slot.id);
		const teacherName = slot.defaultTeacher;
		const issue = slotData ? findSlotAvailabilityIssue(slotData, currentLessons, slotsData) : undefined;

		slot.issue = issue ?? null;
		slot.isBlocked = Boolean(slotData && issue && isSlotBlocked(slotData, currentLessons, slotsData));
		slot.isRoomAvailable = getRoomAvailabilityStatus(slotData, currentLessons);
		slot.isTeacherAvailable = getTeacherAvailabilityStatus(teacherName, slotData, currentLessons);
	});

	return <SlotsLayout slots={extendedSlots as any[]} settings={settings as AppSettings} />;
}

export default SlotsPage;
